<template>
  <!-- Row: Chart -->
  <div class="row f-row prices_chart_height">
    <div class="col col-12" style="height:100%; width:100%">
      <div class="h-100 d-flex flex-column" id="rates-div" style="width: 100%">
        <svg id="svg-rates"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverPlanning } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  colorCall,
  colorExpo,
  colorExpoAlpha4,
  colorFwd,
  colorFwdF,
  colorHedge,
  colorHedgeAlpha3,
  colorNet,
  colorPut,
  currencyFormat2,
  darkCall,
  darkFwd,
  darkPut,
  formatNumber,
  whiteGray,
  whiteGrayAlpha2,
  formatPercent,
  formatM,
} from "@/_core/_helpers/_parametric_tools";
import { select } from "d3";
import { idxOfDate } from "@/_core/data/Advanced";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";

export default {
  name: "PriceChart",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics(),
    },
    colorTheme: { required: true, type: Boolean, default: true },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 },
  },
  components: {},
  data() {
    return {
      chartBodyTA: null,
      chartBodyPCScatter: null,
      chartName: "chart1",
      currencyFormat: currencyFormat2,
      D3Chart: null,
      D3HoverPlanning: null,
      dateFormat: "%b %y", // TODO: que cambie
      derivativeSize: 14,
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      },
      isUpdatingTable: false,
      lCall: require("@/assets/images/lCall.svg"),
      lCallOff: require("@/assets/images/lCallA.svg"),
      lCallOn: require("@/assets/images/lCallH.svg"),
      lFwd: require("@/assets/images/lFwd.svg"),
      lFwdOff: require("@/assets/images/lFwdA.svg"),
      lFwdOn: require("@/assets/images/lFwdH.svg"),
      lPut: require("@/assets/images/lPut.svg"),
      lPutOff: require("@/assets/images/lPutA.svg"),
      lPutOn: require("@/assets/images/lPutH.svg"),
      minMaxYAxis: [],
      showTooltipDeriv: true, // TODO: que cambie
      sCall: require("@/assets/images/sCall.svg"),
      sCallOff: require("@/assets/images/sCallA.svg"),
      sCallOn: require("@/assets/images/sCallH.svg"),
      sFwd: require("@/assets/images/sFwd.svg"),
      sFwdOff: require("@/assets/images/sFwdA.svg"),
      sFwdOn: require("@/assets/images/sFwdH.svg"),
      sPut: require("@/assets/images/sPut.svg"),
      sPutOff: require("@/assets/images/sPutA.svg"),
      sPutOn: require("@/assets/images/sPutH.svg"),
      svgNode: null,
    };
  },
  created() {
    this.D3Chart = new D3Chart("rates-div", "svg-rates");
    this.D3HoverPlanning = new D3HoverPlanning();
  },
  mounted() {
    this.selectHighlightImg();
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.selectHighlightImg();
      this.callWidth();
    },
    renderChartSignal() {
      if (this.Analytics["rawData"]["zoomedData"].length > 0) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    formatVars: {
      handler() {
        if (!this.isUpdatingTable) {
          this.isUpdatingTable = true;
          setTimeout(() => {
            this.isUpdatingTable = false;
            this.onHoverChart();
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    selectHighlightImg() {
      if (this.colorTheme) {
        this.lFwdOn = require("@/assets/images/lFwdH.svg");
        this.sFwdOn = require("@/assets/images/sFwdH.svg");
        this.lCallOn = require("@/assets/images/lCallH.svg");
        this.sCallOn = require("@/assets/images/sCallH.svg");
        this.lPutOn = require("@/assets/images/lPutH.svg");
        this.sPutOn = require("@/assets/images/sPutH.svg");
      } else if (!this.colorTheme) {
        this.lFwdOn = require("@/assets/images/lFwdHB.svg");
        this.sFwdOn = require("@/assets/images/sFwdHB.svg");
        this.lCallOn = require("@/assets/images/lCallHB.svg");
        this.sCallOn = require("@/assets/images/sCallHB.svg");
        this.lPutOn = require("@/assets/images/lPutHB.svg");
        this.sPutOn = require("@/assets/images/sPutHB.svg");
      }
    },
    deleteChart() {
      select("#rates-div")
        .selectAll(".tooltip")
        .remove();
      select("#rates-div")
        .select("svg > *")
        .remove();
    },
    filterShownDerivatives(d) {
      let res = false;
      if (this.Analytics["userSelections"]["showLFwd"]) {
        res =
          res ||
          ((d.buy_sell === "long" || d.buy_sell === "offset short") &&
            (d.derivative === "forward" || d.derivative === "future"));
      }
      if (this.Analytics["userSelections"]["showSFwd"]) {
        res =
          res ||
          ((d.buy_sell === "short" || d.buy_sell === "offset long") &&
            (d.derivative === "forward" || d.derivative === "future"));
      }
      if (this.Analytics["userSelections"]["showLCall"]) {
        res =
          res ||
          ((d.buy_sell === "long" || d.buy_sell === "offset short") &&
            d.derivative === "call");
      }
      if (this.Analytics["userSelections"]["showSCall"]) {
        res =
          res ||
          ((d.buy_sell === "short" || d.buy_sell === "offset long") &&
            d.derivative === "call");
      }
      if (this.Analytics["userSelections"]["showLPut"]) {
        res =
          res ||
          ((d.buy_sell === "long" || d.buy_sell === "offset short") &&
            d.derivative === "put");
      }
      if (this.Analytics["userSelections"]["showSPut"]) {
        res =
          res ||
          ((d.buy_sell === "short" || d.buy_sell === "offset long") &&
            d.derivative === "put");
      }
      return res;
    },
    setMinMaxNoHedges(fldForecast, fldFixed, fldMkt, fldNet) {
      this.minMaxYAxis = [Infinity, -Infinity];
      if (this.Analytics["userSelections"]["showForecast"]) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldForecast,
          fldForecast,
          this.minMaxYAxis,
        );
      }
      if (
        this.Analytics["userSelections"]["showFixedFutures"] ||
        this.Analytics["userSelections"]["showFixedBasis"]
      ) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldFixed,
          fldFixed,
          this.minMaxYAxis,
        );
      }
      if (this.Analytics["userSelections"]["showMarket"]) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldMkt,
          fldMkt,
          this.minMaxYAxis,
        );
      }
      if (this.Analytics["userSelections"][fldNet]) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldNet,
          fldNet,
          this.minMaxYAxis,
        );
      }
    },
    createBarChart() {
      if (this.Analytics["rawData"]["zoomedData"].length === 0) {
        return;
      }
      let _this = this;
      let xRange = this.Analytics["rawData"]["zoomedData"].map((d) => d.date);
      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.5,
        15,
        0.5,
        55,
        0.5,
        25,
        0.5,
        1,
        true,
      );
      if (this.svgNode.height === 0 || this.svgNode.width === 0) {
        return;
      }
      // === auxData and Fields
      let hData = this.Analytics["rawData"]["zoomedDerivatives"];
      let fldForecast = this.Analytics["userSelections"]["fldBudget"];
      let fldFixed = this.Analytics["userSelections"]["fldFixed"];
      let fldMkt = this.Analytics["userSelections"]["fldMkt"];
      let fldNet = this.Analytics["userSelections"]["fldNet"];
      // === minMax
      this.setMinMaxNoHedges(fldForecast, fldFixed, fldMkt, fldNet);
      this.minMaxYAxis = this.processData.minMax(
        hData.filter((d) => _this.filterShownDerivatives(d)),
        "strike",
        "strike",
        this.minMaxYAxis,
      );
      this.minMaxYAxis = this.processData.minMax(
        hData.filter((d) => _this.filterShownDerivatives(d)),
        "barrier",
        "barrier",
        this.minMaxYAxis,
      );
      this.minMaxYAxis = this.processData.minMax(
        hData.filter((d) => _this.filterShownDerivatives(d)),
        "barrier_2",
        "barrier_2",
        this.minMaxYAxis,
      );
      let del = 0.05 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      let del1 = this.minMaxYAxis[0] === 0 ? 0 : del;
      let del2 = this.minMaxYAxis[1] === 0 ? 0 : del;
      this.minMaxYAxis = [
        this.minMaxYAxis[0] - del1,
        this.minMaxYAxis[1] + del2,
      ];

      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        this.Analytics["rawData"]["zoomedData"],
        this.minMaxYAxis,
        this.dateFormat,
        50,
        8,
        false,
        [],
        true,
        true,
        false,
        this.currencyFormat,
        false,
      );
      // ==== zoom setup
      this.chartBodyTA = this.svgNode
        .append("g")
        .attr("class", "chartBodyTA")
        .attr("clip-path", `url(#clipTA${this.chartName})`);
      this.svgNode
        .append("defs")
        .append("clipPath")
        .attr("id", "clipTA" + this.chartName)
        .append("rect")
        .attr("width", this.svgNode.width)
        .attr("height", this.svgNode.height);

      // if (this.Analytics["rawData"]["zoomedData"].length === 0) {
      // const barWidth = 0.7;
      this.D3Chart.backgroundRect(
        this.svgNode,
        this.Analytics["rawData"]["zoomedData"].map((d) => d.date),
        this.Analytics["rawData"]["zoomedData"].map((d, i) => i % 2 !== 0),
        "PLAN",
        0,
        0,
        this.chartBodyTA,
      );
      // }

      // if (this.Analytics["rawData"]["zoomedData"].length > 0) {
      //   // === alpha candles
      //   this.svgNode = this.D3Chart.candles(
      //     this.svgNode,
      //     this.Analytics["rawData"]["zoomedData"],
      //     colorExpoAlpha3(this.colorTheme),
      //     grayLightBackgroundAlpha2(this.colorTheme),
      //     "TA",
      //     0.8,
      //     0.8,
      //     null,
      //     null,
      //     1,
      //     true,
      //     xRange.length > 49 ? 0.9 : xRange.length > 19 ? 1 / 5 : 1 / 21,
      //     xRange,
      //     false,
      //     "",
      //     false,
      //     "stick",
      //     "stick2",
      //     this.chartBodyTA
      //   );
      // }

      if (this.Analytics["userSelections"]["showSimulations"]) {
        if (this.Analytics["userSelections"]["showForecast"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.simulations,
            1,
            "RATEES",
            "rgba(0,0,0,0)",
            colorExpoAlpha4(this.colorTheme),
            "date",
            "m2m_rate_up",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            true,
            "m2m_rate_dn",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showFixedFutures"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.simulations,
            0.5,
            "RATEHS",
            "rgba(0,0,0,0)",
            colorHedgeAlpha3(this.colorTheme),
            "date",
            "hedge_rate_up",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            true,
            "hedge_rate_dn",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showNet"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.simulations,
            0.25,
            "RATENS",
            "rgba(0,0,0,0)",
            whiteGrayAlpha2(this.colorTheme),
            "date",
            "net_rate_up",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            true,
            "net_rate_dn",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
      }

      if (this.Analytics["userSelections"]["showForecast"]) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.7,
          "RATEE",
          colorExpo(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          fldForecast,
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }
      if (
        this.Analytics["userSelections"]["includeFutures"] &&
        this.Analytics["userSelections"]["showFixedFutures"]
      ) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.6,
          "RATEH",
          colorFwdF(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          fldFixed,
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }
      if (
        this.Analytics["userSelections"]["includeRatio"] &&
        this.Analytics["userSelections"]["showFixedBasis"]
      ) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.4,
          "RATEH2",
          colorHedge(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          fldFixed,
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }
      if (this.Analytics["userSelections"]["showMarket"]) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.8,
          "RATEE45",
          whiteGray(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          fldMkt,
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }
      if (this.Analytics["userSelections"]["showNet"]) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.5,
          "RATEN",
          colorNet(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          fldNet,
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }

      if (this.Analytics["userSelections"]["showAve"]) {
        if (this.Analytics["userSelections"]["showLFwd"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAverages,
            0.95,
            "DLF",
            colorFwd,
            "rgba(0,0,0,0)",
            "date",
            "lFwd",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showSFwd"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAverages,
            0.9,
            "DSF",
            darkFwd,
            "rgba(0,0,0,0)",
            "date",
            "sFwd",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showLCall"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAverages,
            0.85,
            "DLC",
            colorCall,
            "rgba(0,0,0,0)",
            "date",
            "lCall",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showSCall"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAverages,
            0.8,
            "DSC",
            darkCall,
            "rgba(0,0,0,0)",
            "date",
            "sCall",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showLPut"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAverages,
            0.75,
            "DLP",
            colorPut,
            "rgba(0,0,0,0)",
            "date",
            "lPut",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.Analytics["userSelections"]["showSPut"]) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAverages,
            0.7,
            "DSP",
            darkPut,
            "rgba(0,0,0,0)",
            "date",
            "sPut",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
      }

      const zoomedData = this.Analytics["rawData"]["zoomedData"];
      // === Filter data of derivates charts
      const filterDataLfwd = hData.filter(
        (d) =>
          (d.buy_sell === "long" || d.buy_sell === "offset short") &&
          (d.derivative === "forward" || d.derivative === "future"),
      );
      const filterDtaSFwd = hData.filter(
        (d) =>
          (d.buy_sell === "short" || d.buy_sell === "offset long") &&
          (d.derivative === "forward" || d.derivative === "future"),
      );
      const filterDataLCall = hData.filter(
        (d) =>
          (d.buy_sell === "long" || d.buy_sell === "offset short") &&
          d.derivative === "call",
      );
      const filterDataSCall = hData.filter(
        (d) =>
          (d.buy_sell === "short" || d.buy_sell === "offset long") &&
          d.derivative === "call",
      );
      const filterDataLPut = hData.filter(
        (d) =>
          (d.buy_sell === "long" || d.buy_sell === "offset short") &&
          d.derivative === "put",
      );
      const filterDataSPut = hData.filter(
        (d) =>
          (d.buy_sell === "short" || d.buy_sell === "offset long") &&
          d.derivative === "put",
      );

      // ===  Hover configuration data
      const objSimulations = {
        isShow: "showSimulations",
        showFields: ["showForecast", "showFixedFutures", "showNet"],
        data: this.simulations,
        customNameBars: ["RATEES", "RATEHS", "RATENS"],
        yFields: ["m2m_rate_up", "hedge_rate_up", "net_rate_up"],
      };
      const objZoomedData = {
        showFields: [
          "showForecast",
          ["includeFutures", "showFixedFutures"],
          ["includeRatio", "showFixedBasis"],
          "showMarket",
          "showNet",
        ],
        data: zoomedData,
        customNameBars: ["RATEE", "RATEH", "RATEH2", "RATEE45", "RATEN"],
        yFields: [fldForecast, fldFixed, fldFixed, fldMkt, fldNet],
      };
      const objSHowAve = {
        isShow: "showAve",
        showFields: [
          "showLFwd",
          "showSFwd",
          "showLCall",
          "showSCall",
          "showLPut",
          "showSPut",
        ],
        data: this.derivativeAverages,
        customNameBars: ["DLF", "DSF", "DLC", "DSC", "DLP", "DSP"],
        yFields: ["lFwd", "sFwd", "lCall", "sCall", "lPut", "sPut"],
      };
      const objNonShowAve = {
        isShow: "showAve",
        showFields: [
          "showLFwd",
          "showSFwd",
          "showLCall",
          "showSCall",
          "showLPut",
          "showSPut",
        ],
        data: [
          filterDataLfwd,
          filterDtaSFwd,
          filterDataLCall,
          filterDataSCall,
          filterDataLPut,
          filterDataSPut,
        ],
        customName: [
          "longFwds",
          "shortFwds",
          "longCalls",
          "shortCalls",
          "longPuts",
          "shortPuts",
        ],
      };
      const dataBars = [objSimulations, objSHowAve, objZoomedData];
      const dataScatter = [objNonShowAve];

      const figuresElemsHover = [
        { type: "bars", data: dataBars },
        { type: "twoScatters", data: dataScatter },
      ];
      // === hover fx ===
      this.D3HoverPlanning.hoverFct(
        this.svgNode,
        this.Analytics["rawData"]["zoomedData"],
        "RATES",
        formatNumber,
        this.currency,
        formatNumber,
        this.dateFormat,
        this.currencyFormat,
        this.budgetRate,
        formatPercent,
        // this.showTooltip,
        false,
        formatM,
        this.averages,
        this.showAve,
        this.asset,
        this.isFuture,
        this.colorTheme,
        true,
        this,
        "date",
        true,
        figuresElemsHover,
        "render",
        this.Analytics["rawData"],
      );
      // === Body derivates charts
      this.chartBodyPCScatter = this.svgNode
        .append("g")
        .attr("class", "chartBodyPCScatter")
        .attr("clip-path", `url(#clipPCScatter)`);
      this.svgNode
        .append("defs")
        .append("clipPath")
        .attr("id", "clipPCScatter")
        .append("rect")
        .attr("width", this.svgNode.width)
        .attr("height", this.svgNode.height);

      // === create derivatives charts ===
      if (
        hData.length > 0 &&
        !this.Analytics["userSelections"]["showAve"] &&
        (this.Analytics["userSelections"]["showLFwd"] ||
          this.Analytics["userSelections"]["showSFwd"] ||
          this.Analytics["userSelections"]["showLCall"] ||
          this.Analytics["userSelections"]["showSCall"] ||
          this.Analytics["userSelections"]["showLPut"] ||
          this.Analytics["userSelections"]["showSPut"])
      ) {
        let dates = hData.map((d) => +d.date);
        hData.forEach(function(d) {
          d.historicalIndex = idxOfDate(dates, +d.trade_date);
        });
      } else {
        hData.forEach(function(d) {
          d.historicalIndex = -1;
        });
      }

      // lFwd
      if (!this.Analytics["userSelections"]["showAve"]) {
        if (this.Analytics["userSelections"]["showLFwd"]) {
          this.D3Chart.scatterImg(
            this.svgNode,
            filterDataLfwd,
            "longFwds",
            "LFWD",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.lFwd,
            this.lFwdOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.Analytics["rawData"]["zoomedData"].length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyPCScatter,
            true,
          );
        }
        // sFwd
        if (this.Analytics["userSelections"]["showSFwd"]) {
          this.D3Chart.scatterImg(
            this.svgNode,
            filterDtaSFwd,
            "shortFwds",
            "SFWD",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.sFwd,
            this.sFwdOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.Analytics["rawData"]["zoomedData"].length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyPCScatter,
            true,
          );
        }
        // lCall
        if (this.Analytics["userSelections"]["showLCall"]) {
          this.D3Chart.scatterImg(
            this.svgNode,
            filterDataLCall,
            "longCalls",
            "LCALL",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.lCall,
            this.lCallOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.Analytics["rawData"]["zoomedData"].length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyPCScatter,
            true,
          );
        }
        // sCall
        if (this.Analytics["userSelections"]["showSCall"]) {
          this.D3Chart.scatterImg(
            this.svgNode,
            filterDataSCall,
            "shortCalls",
            "SCALL",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.sCall,
            this.sCallOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.Analytics["rawData"]["zoomedData"].length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyPCScatter,
            true,
          );
        }
        // lPut
        if (this.Analytics["userSelections"]["showLPut"]) {
          this.D3Chart.scatterImg(
            this.svgNode,
            filterDataLPut,
            "longPuts",
            "LPUT",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.lPut,
            this.lPutOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.Analytics["rawData"]["zoomedData"].length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyPCScatter,
            true,
          );
        }
        // sPut
        if (this.Analytics["userSelections"]["showSPut"]) {
          this.D3Chart.scatterImg(
            this.svgNode,
            filterDataSPut,
            "shortPuts",
            "SPUT",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.sPut,
            this.sPutOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.Analytics["rawData"]["zoomedData"].length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyPCScatter,
            true,
          );
        }
      }
    },
    onHoverChart() {
      this.$emit("onTableFormat", this.formatVars.userHovered);
    },
    callWidth() {
      if (this.Analytics["rawData"]["zoomedData"].length > 0) {
        this.createBarChart();
      }
    },
  },
};
</script>

<style scoped></style>
